import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Voicemail, Play, Loader2, PhoneIncoming } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { voiceAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import RecordingPlayerDialog from '@/components/voice/RecordingPlayerDialog';

export default function VoicemailInboxPage() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [canPlay, setCanPlay] = useState(false);
  const [loading, setLoading] = useState(true);
  const [playerVm, setPlayerVm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await voiceAPI.voicemails();
      setItems(r.data.items || []);
      setUnread(r.data.unread || 0);
      setCanPlay(!!r.data.can_access_recordings);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not load voicemails'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const play = async (vm) => {
    if (!vm.recording_sid) { toast.error('Recording not ready yet'); return; }
    setPlayerVm(vm);
    // Mark as read on open.
    if (!vm.voicemail_read) {
      try {
        await voiceAPI.markVoicemailRead(vm.call_sid);
        setItems((prev) => prev.map((x) => x.call_sid === vm.call_sid ? { ...x, voicemail_read: true } : x));
        setUnread((u) => Math.max(0, u - 1));
      } catch (_) { /* noop */ }
    }
  };

  return (
    <div data-testid="voicemail-inbox-page">
      <PageHeader
        title="Voicemails"
        description="Messages left by callers when nobody answered in the CRM."
        breadcrumbs={[{ label: 'Voicemails', href: '/voicemails' }]}
      />
      <div className="page-content space-y-4 max-w-3xl">
        <div className="flex items-center gap-2">
          <Badge className="bg-slate-100 text-slate-700" data-testid="voicemail-total">{items.length} total</Badge>
          {unread > 0 && <Badge className="bg-rose-100 text-rose-700" data-testid="voicemail-unread">{unread} new</Badge>}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <Card><CardContent className="py-14 text-center text-sm text-slate-500" data-testid="voicemail-empty">
            <Voicemail size={22} className="mx-auto mb-2 text-slate-300" />
            No voicemails yet.
          </CardContent></Card>
        ) : (
          <div className="space-y-2">
            {items.map((vm) => (
              <Card key={vm.call_sid} className={vm.voicemail_read ? '' : 'border-rose-200 bg-rose-50/40'} data-testid={`voicemail-row-${vm.call_sid}`}>
                <CardContent className="p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center shrink-0">
                      <PhoneIncoming size={15} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm font-mono">{vm.from_number || 'Unknown'}</span>
                        {!vm.voicemail_read && <Badge className="bg-rose-100 text-rose-700 text-[10px]">NEW</Badge>}
                      </div>
                      <div className="text-xs text-slate-500">{vm.created_at ? new Date(vm.created_at).toLocaleString() : ''}{vm.recording_duration ? ` · ${vm.recording_duration}s` : ''}</div>
                      {vm.note && <div className="text-xs text-slate-600 mt-0.5 truncate">Note: {vm.note}</div>}
                    </div>
                  </div>
                  {canPlay ? (
                    <Button size="sm" variant="outline" onClick={() => play(vm)} disabled={!vm.recording_sid} data-testid={`voicemail-play-${vm.call_sid}`}>
                      <Play size={14} className="mr-1" /> Play
                    </Button>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-500 text-[10px]">No access</Badge>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <RecordingPlayerDialog
        open={!!playerVm}
        recordingSid={playerVm?.recording_sid}
        title="Voicemail"
        subtitle={playerVm ? `From ${playerVm.from_number || 'Unknown'}` : ''}
        onClose={() => setPlayerVm(null)}
      />
    </div>
  );
}
